import { parseDate } from '@openphone/lib/date'
import { action, makeObservable, observable, toJS } from 'mobx'

import type IntegrationStore from '@src/service/integration-store'
import type { Model } from '@src/service/model/base'
import type { IntegrationEvent } from '@src/service/transport/integration'

import type { LegacyIntegration } from './types'

// Common interface for legacy integrations (Slack and Email)
export interface BaseLegacyIntegration {
  id: string
  type: string
  events: Partial<IntegrationEvent>
  phoneNumberId: string | null
  createdAt: number | null
}

// Base class for legacy integration models (Slack and Email)
export abstract class BaseLegacyIntegrationModel<T extends BaseLegacyIntegration>
  implements Model, BaseLegacyIntegration
{
  protected raw: T

  constructor(
    protected integrationStore: IntegrationStore,
    initialValues: T,
    attrs: Partial<T> = {},
  ) {
    this.raw = { ...initialValues }
    this.deserialize(attrs)

    makeObservable<this, any>(this, {
      raw: observable,
      update: action,
      delete: action,
      deserialize: action,
      serialize: action,
    })
  }

  get id(): string {
    return this.raw.id
  }

  // Using function declaration to allow for implementation without override in derived classes
  abstract get type(): string

  get events(): Partial<IntegrationEvent> {
    return this.raw.events
  }

  get phoneNumberId(): string | null {
    return this.raw.phoneNumberId
  }

  get createdAt(): number | null {
    return this.raw.createdAt
  }

  update(attrs: Partial<T>): this {
    Object.assign(this.raw, attrs)
    void this.integrationStore.updateLegacy(this as unknown as LegacyIntegration)
    return this
  }

  delete(): void {
    void this.integrationStore.deleteLegacy(this as unknown as LegacyIntegration)
  }

  deserialize(json: Partial<T>): this {
    Object.assign(this.raw, json)

    if (json.createdAt) {
      this.raw.createdAt = parseDate(json.createdAt)
    }

    return this
  }

  serialize(): T {
    return toJS(this.raw)
  }
}
